import {BasicDataResourceToQueriesMap} from '@/modules/basic-data/constants'
import {Anchor, createTopAnchor} from '@cypro/ui/components/ui/infinite-data-table'
import {ListBasicDataInput} from '@cypro/engine/queries'
import {ApplicationForm, BasicDataItem, BasicDataResource, BasicDataResources, GeneralPhysician, MedicineForm} from '@cypro/engine/schema'
import {useQuery} from '@rocicorp/zero/react'
import {useEffect, useMemo, useState} from 'react'

/**
 * Maps special data types, with different fields from a `BasicDataItem`.
 */
type ResourceItemMap = {
  [BasicDataResources.medicineForms]: MedicineForm
  [BasicDataResources.applicationForms]: ApplicationForm
  [BasicDataResources.generalPhysicians]: GeneralPhysician
}

type ResolveItem<T extends BasicDataResource> = T extends keyof ResourceItemMap ? ResourceItemMap[T] : BasicDataItem

interface Config<R extends BasicDataResource> extends Omit<Partial<ListBasicDataInput>, 'cursor'> {
  resource: R
  hasProtocols?: boolean
}

export function useGetBasicDataItems<R extends BasicDataResource>({
  sort = {dir: 'asc', field: 'name'},
  filters,
  limit,
  resource,
  hasProtocols,
}: Config<R>) {
  type CurrentItem = ResolveItem<R>

  const [anchor, setAnchor] = useState<Anchor<CurrentItem>>(createTopAnchor<CurrentItem>())

  const cursor =
    anchor.startRow?.id && anchor.startRow?.createdAt
      ? {
          id: anchor.startRow.id,
          direction: anchor.direction,
          name: anchor.startRow.name,
          createdAt: anchor.startRow.createdAt,
        }
      : undefined

  const queries = BasicDataResourceToQueriesMap[resource]
  const query = queries.listItems({
    cursor,
    sort,
    filters: {status: ['active'] as const, ...filters},
    limit,
    hasProtocols,
  })
  const [items, meta] = useQuery(query)
  const filtersHash = useMemo(() => JSON.stringify(filters), [filters])

  useEffect(() => {
    setAnchor(createTopAnchor<CurrentItem>())
  }, [sort?.dir, sort?.field, filtersHash, limit])

  return {
    query,
    meta,
    items: items as unknown as CurrentItem[],
    anchor,
    fetchMore: setAnchor,
    canFetchMore: items.length === limit,
    isFetching: meta.type === 'unknown',
  }
}
